/**
 * The voice languages to download (`wantedPocketLanguages`), from everything
 * that says which languages the user uses: the dictation language, the
 * languages they speak, the app language, their keyboards and the computer's
 * languages. Provisioning and the Settings Voice line read this one answer.
 *
 * The keyboards' languages are asked of the desktop app once per mount; until
 * it answers the list is `null`, so nothing is downloaded on a partial answer.
 */
import { invoke } from "@tauri-apps/api/core";
import { useEffect, useMemo, useState } from "react";

import { isTauri } from "@/lib/is-tauri";
import { useEffectiveAppLanguage } from "@/stores/app-language-store";
import {
  spokenLanguagesOrDefault,
  useDictationSettingsStore,
} from "@/stores/dictation-settings-store";
import { wantedPocketLanguages, type PocketLanguage } from "./pocket-voice";

const NO_KEYBOARDS: readonly string[] = [];

/** The keyboards' languages, `null` until the desktop app answers. */
export function useKeyboardLanguages(): readonly string[] | null {
  const [languages, setLanguages] = useState<readonly string[] | null>(() =>
    isTauri() ? null : NO_KEYBOARDS,
  );
  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    void invoke<string[]>("denker_keyboard_languages")
      .catch(() => NO_KEYBOARDS)
      .then((answer) => {
        if (!cancelled)
          setLanguages(Array.isArray(answer) ? answer : NO_KEYBOARDS);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return languages;
}

/** The same array until the answer changes, so effects keyed on it run only
 *  when a language is added or dropped. */
export function useWantedPocketLanguages(): PocketLanguage[] | null {
  const dictationLocale = useDictationSettingsStore((s) => s.locale);
  const savedSpoken = useDictationSettingsStore((s) => s.spokenLanguages);
  const appLanguage = useEffectiveAppLanguage();
  const keyboardLanguages = useKeyboardLanguages();
  const key =
    keyboardLanguages &&
    wantedPocketLanguages({
      dictationLocale,
      spokenLanguages: spokenLanguagesOrDefault(savedSpoken, appLanguage),
      appLanguage,
      keyboardLanguages,
    }).join(" ");
  return useMemo(
    () => (key === null ? null : (key.split(" ") as PocketLanguage[])),
    [key],
  );
}
